import type {
  ActivityKind,
  ActivityLogEntry,
  BaselineSnapshot,
  DailySummary,
  LessonResult,
  LifetimeStats,
  UserProfile,
} from '../types';
import { isExtraTrin, localDateFromIso, pruneDailySummaries } from './stats';

export const ACTIVITY_LOG_CAP = 250;

export interface ActivityPayload {
  kind: ActivityKind;
  at: string;
  durationMs: number;
  totalKeystrokes: number;
  correctChars: number;
  wpm?: number;
  accuracy?: number;
  trinId?: string;
  checkpointId?: string;
  xpGained?: number;
}

export function emptyLifetimeStats(): LifetimeStats {
  return {
    totalKeystrokes: 0,
    correctChars: 0,
    sessionCount: 0,
    activityByKind: {},
  };
}

export function activityKindFromLesson(result: LessonResult): ActivityKind {
  return isExtraTrin(result.trinId) ? 'extra-lesson' : 'curriculum-lesson';
}

export function activityPayloadFromLesson(
  result: LessonResult,
  xpGained: number,
): ActivityPayload {
  return {
    kind: activityKindFromLesson(result),
    at: result.startedAt,
    durationMs: result.durationMs,
    totalKeystrokes: result.totalKeystrokes,
    correctChars: result.correctChars,
    wpm: result.wpm,
    accuracy: result.accuracy,
    trinId: result.trinId,
    xpGained,
  };
}

export function activityPayloadFromBaseline(
  snapshot: BaselineSnapshot,
): ActivityPayload {
  return {
    kind:
      snapshot.kind === 'transcription'
        ? 'baseline-transcription'
        : 'baseline-composition',
    at: snapshot.takenAt,
    durationMs: snapshot.durationMs,
    totalKeystrokes: snapshot.totalKeystrokes,
    correctChars: snapshot.correctChars,
    wpm: snapshot.wpm,
    accuracy: snapshot.accuracy,
  };
}

export function normalizeDailySummary(s: DailySummary): DailySummary {
  return {
    ...s,
    totalKeystrokes: s.totalKeystrokes ?? 0,
    correctChars: s.correctChars ?? 0,
    sessionCount: s.sessionCount ?? s.lessonCount ?? 0,
    activityByKind: s.activityByKind ?? {},
  };
}

export function mergeActivityIntoDailySummary(
  prev: DailySummary | undefined,
  payload: ActivityPayload,
  date: string,
  xpGained = 0,
): DailySummary {
  const base = prev ? normalizeDailySummary(prev) : undefined;
  const activityByKind = { ...(base?.activityByKind ?? {}) };
  activityByKind[payload.kind] = (activityByKind[payload.kind] ?? 0) + 1;

  return {
    date,
    lessonCount: base?.lessonCount ?? 0,
    curriculumLessonCount: base?.curriculumLessonCount ?? 0,
    avgWpm: base?.avgWpm ?? 0,
    avgAccuracy: base?.avgAccuracy ?? 0,
    totalDurationMs: (base?.totalDurationMs ?? 0) + payload.durationMs,
    bestWpm: base?.bestWpm ?? 0,
    xpGained: (base?.xpGained ?? 0) + xpGained,
    totalKeystrokes: (base?.totalKeystrokes ?? 0) + payload.totalKeystrokes,
    correctChars: (base?.correctChars ?? 0) + payload.correctChars,
    sessionCount: (base?.sessionCount ?? 0) + 1,
    activityByKind,
  };
}

function mergeLifetimeStats(
  prev: LifetimeStats | undefined,
  payload: ActivityPayload,
): LifetimeStats {
  const base = prev ?? emptyLifetimeStats();
  const activityByKind = { ...base.activityByKind };
  activityByKind[payload.kind] = (activityByKind[payload.kind] ?? 0) + 1;
  return {
    totalKeystrokes: base.totalKeystrokes + payload.totalKeystrokes,
    correctChars: base.correctChars + payload.correctChars,
    sessionCount: base.sessionCount + 1,
    activityByKind,
  };
}

function appendActivityLog(
  log: ActivityLogEntry[] | undefined,
  payload: ActivityPayload,
): ActivityLogEntry[] {
  const entry: ActivityLogEntry = {
    id: `${payload.at}-${payload.kind}-${Math.random().toString(36).slice(2, 9)}`,
    kind: payload.kind,
    at: payload.at,
    durationMs: payload.durationMs,
    totalKeystrokes: payload.totalKeystrokes,
    correctChars: payload.correctChars,
    wpm: payload.wpm,
    accuracy: payload.accuracy,
    trinId: payload.trinId,
    checkpointId: payload.checkpointId,
    xpGained: payload.xpGained,
  };
  return [entry, ...(log ?? [])].slice(0, ACTIVITY_LOG_CAP);
}

export interface RecordActivityOptions {
  /** Lektioner opdaterer daglig rollup via mergeLessonIntoDailySummary. */
  skipDaily?: boolean;
}

/** Registrér en afsluttet øvelse: livstid, log og evt. daglig rollup. */
export function recordActivity(
  user: UserProfile,
  payload: ActivityPayload,
  opts: RecordActivityOptions = {},
): UserProfile {
  const date = localDateFromIso(payload.at);
  let dailySummaries = user.dailySummaries;
  if (!opts.skipDaily) {
    dailySummaries = pruneDailySummaries({
      ...(dailySummaries ?? {}),
      [date]: mergeActivityIntoDailySummary(
        dailySummaries?.[date],
        payload,
        date,
        payload.xpGained ?? 0,
      ),
    });
  }

  return {
    ...user,
    dailySummaries,
    lifetimeStats: mergeLifetimeStats(user.lifetimeStats, payload),
    activityLog: appendActivityLog(user.activityLog, payload),
    lastActiveAt: new Date().toISOString(),
  };
}

/** Estimat for ord-baserede øvelser uden tastetælling. */
export function estimateKeystrokesFromWords(wordCount: number): {
  totalKeystrokes: number;
  correctChars: number;
} {
  const chars = Math.max(0, wordCount) * 5;
  return { totalKeystrokes: chars, correctChars: chars };
}

export function formatKeystrokeCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}k`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(Math.round(n));
}

export function sumDailyKeystrokes(
  summaries: Record<string, DailySummary> | undefined,
): number {
  if (!summaries) return 0;
  return Object.values(summaries).reduce(
    (a, s) => a + (normalizeDailySummary(s).totalKeystrokes ?? 0),
    0,
  );
}
